import type { CatalogItem, Tenant } from "@albertine/types/openapi/lmt";
import classNames from "classnames";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useComponentState, useDebounce } from "../../utils/customHook.util";
import { Flex } from "../Common/Flex";
import Input, { type InputActions } from "../Common/Input";
import ArticleThumbnail from "./ArticleThumbnail";
import "./ArticleSearchInput.css";
import { useGet } from "@albertine/api-client";
import HandleClicksOutside from "../HandleClicksOutside";

const minimumSearchTermChars = 3;

function findSelectedHit(hits: CatalogItem[], selectedHitId: string) {
    const index = hits.findIndex((h) => h.id === selectedHitId);
    return index >= 0 ? { index, hit: hits[index] } : null;
}

type ArticleSearchInputProps = {
    label: string;
    disabled?: boolean;
    searchMode: "all" | "only-published";
    discoverableForTenant?: Tenant | "all";
    onSelect: (selection: CatalogItem) => void;
};

type ArticleSearchInputState = {
    searchTerm: string;
    hitsOpen: boolean;
    selectedHitId: string | null;
};

function ArticleSearchInput(props: ArticleSearchInputProps) {
    const {
        label,
        disabled = false,
        searchMode,
        discoverableForTenant,
        onSelect,
    } = props;
    const { state, setState } = useComponentState<ArticleSearchInputState>({
        searchTerm: "",
        hitsOpen: true,
        selectedHitId: null,
    });
    const debouncedSearchTerm = useDebounce<string>(state.searchTerm, 250);

    const inputRef = useRef<InputActions>(null);
    const [isFocused, setIsFocused] = useState(false);

    const searchQueryEnabled = debouncedSearchTerm.length >= minimumSearchTermChars;

    const { data } = useGet("catalogItemsSearch", {
        query: {
            nameSearchTerm: debouncedSearchTerm,
            published: searchMode === "only-published" ? "true" : undefined,
            discoverableForTenant,
            categories: [],
            ids: [],
            supplierLmtIds: [],
            sources: [],
            tags: [],
            pageSize: "4",
        },
    });

    const hits = searchQueryEnabled && data?.catalogItems ? data.catalogItems : null;
    const hitsCount = hits?.length ?? 0;
    const currentSelection =
        hits && state.selectedHitId ? findSelectedHit(hits, state.selectedHitId) : null;

    useEffect(() => {
        if (!searchQueryEnabled) {
            setState({ selectedHitId: null });
            return;
        }
        if (hitsCount > 0) {
            setState({ hitsOpen: true });
        }
    }, [hitsCount, searchQueryEnabled]);

    const selectHit = (hit: CatalogItem) => {
        setState({
            hitsOpen: false,
            searchTerm: "",
            selectedHitId: null,
        });
        inputRef.current?.reset();
        inputRef.current?.focus();
        onSelect(hit);
    };

    const onSelectHit = useEffectEvent((hit: CatalogItem) => {
        setState({
            hitsOpen: false,
            searchTerm: "",
            selectedHitId: null,
        });
        inputRef.current?.reset();
        inputRef.current?.focus();
        onSelect(hit);
    });

    const onSetCurrentSelection = useEffectEvent((direction: "up" | "down") => {
        if (!hits || hits.length <= 0) {
            setState({ selectedHitId: null });
            return;
        }
        const nextIndex =
            direction === "down"
                ? (currentSelection?.index === undefined
                      ? -1
                      : currentSelection.index) + 1
                : (currentSelection?.index === undefined ? 1 : currentSelection.index) -
                  1;

        const nextItem = hits[nextIndex];

        setState({
            selectedHitId: nextItem ? nextItem.id : hits[0].id,
        });
    });

    const onHandleEnter = useEffectEvent(() => {
        if (currentSelection) {
            onSelectHit(currentSelection.hit);
        }
    });

    useEffect(() => {
        if (!isFocused) {
            return;
        }

        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === "ArrowUp") {
                onSetCurrentSelection("up");
                event.stopPropagation();
                event.preventDefault();
            }

            if (event.key === "ArrowDown") {
                onSetCurrentSelection("down");
                event.stopPropagation();
                event.preventDefault();
            }

            if (event.key === "Enter") {
                onHandleEnter();
                event.stopPropagation();
                event.preventDefault();
            }
        };

        window.addEventListener("keydown", handleKeyPress);

        return () => {
            window.removeEventListener("keydown", handleKeyPress);
        };
    }, [isFocused]);

    return (
        <Flex.Row className="article-search-input">
            <HandleClicksOutside
                className="article-search-input__outside-clicks"
                disabled={!state.hitsOpen || !hits?.length}
                onClickOutside={() => {
                    setState({ hitsOpen: false });
                }}
            >
                <Input.PersistentTextField
                    ref={inputRef}
                    label={label}
                    value={state.searchTerm}
                    onChange={(searchTerm) => {
                        setState({ searchTerm });
                    }}
                    onFocus={(_value) => {
                        setIsFocused(true);
                        if (!state.hitsOpen) {
                            setState({ hitsOpen: true });
                        }
                    }}
                    onBlur={(_value) => {
                        setIsFocused(false);
                    }}
                    disabled={disabled}
                />

                {hits && state.hitsOpen && (
                    <Flex.Column className="article-search-input__hits">
                        {hits.map((hit, index) => (
                            <Flex.Row
                                key={`article-search-${hit.id}`}
                                className={classNames("article-search-input__hit", {
                                    "article-search-input__hit--selected":
                                        currentSelection?.index === index,
                                })}
                                onClick={() => {
                                    selectHit(hit);
                                }}
                            >
                                <ArticleThumbnail article={hit} />
                            </Flex.Row>
                        ))}
                    </Flex.Column>
                )}
            </HandleClicksOutside>
        </Flex.Row>
    );
}

export default ArticleSearchInput;
